import React from "react";

/**
 * 開場・開演時間
 */
type OpeningTime = {
	id: string;
	external_id?: string; // バックエンドで管理されるexternal_id
	openTime: string;
	startTime: string;
	endTime: string;
	description?: string;
};

type OpeningTimesProps = {
	openingTimes: OpeningTime[];
	handleOpeningTimeChange: (
		id: string,
		field: keyof OpeningTime,
		value: string
	) => void;
	addOpeningTime: () => void;
	removeOpeningTime: (id: string) => void;
	handleStartTimeChange: (id: string, value: string) => void;
};

// 以下は時間指定 UI の例
const generateHourOptions = () => {
	const options = [];
	for (let hour = 0; hour <= 23; hour++) {
		const formattedHour = String(hour).padStart(2, "0");
		options.push({
			value: formattedHour,
			label: `${formattedHour}時`,
			hourValue: hour,
		});
	}
	// 24～30は翌日扱い（例：25時 → 翌1時）
	for (let hour = 24; hour <= 30; hour++) {
		const formattedHour = String(hour).padStart(2, "0");
		const displayHour = hour - 24;
		options.push({
			value: formattedHour,
			label: `${formattedHour}時 (翌${displayHour}時)`,
			hourValue: hour,
		});
	}
	return options;
};

const generateMinuteOptions = () => {
	const options = [];
	for (let minute = 0; minute < 60; minute += 5) {
		const formattedMinute = String(minute).padStart(2, "0");
		options.push({ value: formattedMinute, label: `${formattedMinute}分` });
	}
	return options;
};

const hourOptions = generateHourOptions();
const minuteOptions = generateMinuteOptions();

const OpeningTimes: React.FC<OpeningTimesProps> = ({
	openingTimes,
	handleOpeningTimeChange,
	addOpeningTime,
	removeOpeningTime,
	handleStartTimeChange,
}) => {
	// 初期レンダリング時に external_id の状態をログ出力
	React.useEffect(() => {
		console.log(
			"Opening times with external_ids:",
			openingTimes.map((ot) => ({
				id: ot.id,
				external_id: ot.external_id,
				hasExternalId: !!ot.external_id,
			}))
		);
	}, [openingTimes]);
	const extractTimeComponents = (timeString: string) => {
		const [hour, minute] = timeString.split(":");
		return { hour: hour || "00", minute: minute || "00" };
	};

	const combineTimeComponents = (hour: string, minute: string) => {
		return `${hour}:${minute}`;
	};

	const getTimeInMinutes = (timeString: string) => {
		const [hour, minute] = timeString.split(":").map(Number);
		return hour * 60 + minute;
	};

	const formatTimeFromMinutes = (minutes: number) => {
		const hour = Math.floor(minutes / 60);
		const minute = minutes % 60;
		return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
			2,
			"0"
		)}`;
	};

	// 開場時間が変わったときの自動調整
	const handleOpenTimeChange = (timeId: string, newOpenTime: string) => {
		const currentTime = openingTimes.find((time) => time.id === timeId);
		if (!currentTime) return;

		const oldOpenMinutes = getTimeInMinutes(currentTime.openTime);
		const newOpenMinutes = getTimeInMinutes(newOpenTime);
		const timeDiff = newOpenMinutes - oldOpenMinutes;

		if (timeDiff !== 0) {
			const oldStartMinutes = getTimeInMinutes(currentTime.startTime);
			let newStartMinutes = oldStartMinutes + timeDiff;
			newStartMinutes = Math.max(newStartMinutes, newOpenMinutes + 5);
			newStartMinutes = Math.ceil(newStartMinutes / 5) * 5;

			const oldEndMinutes = getTimeInMinutes(currentTime.endTime);
			let newEndMinutes = oldEndMinutes + timeDiff;
			newEndMinutes = Math.max(newEndMinutes, newStartMinutes + 5);
			newEndMinutes = Math.ceil(newEndMinutes / 5) * 5;

			handleOpeningTimeChange(timeId, "openTime", newOpenTime);
			handleStartTimeChange(timeId, formatTimeFromMinutes(newStartMinutes));
			handleOpeningTimeChange(
				timeId,
				"endTime",
				formatTimeFromMinutes(newEndMinutes)
			);
		} else {
			handleOpeningTimeChange(timeId, "openTime", newOpenTime);
		}
	};

	const shouldDisableHour = (
		hourValue: number,
		timeType: "open" | "start" | "end",
		currentTimeId: string
	) => {
		if (timeType === "open") return false;
		const currentTime = openingTimes.find((time) => time.id === currentTimeId);
		if (!currentTime) return false;

		const openTimeMinutes = getTimeInMinutes(currentTime.openTime);
		const startTimeMinutes = getTimeInMinutes(currentTime.startTime);

		if (timeType === "start") {
			const hourMaxMinutes = hourValue * 60 + 55;
			return hourMaxMinutes < openTimeMinutes + 5;
		}
		if (timeType === "end") {
			const hourMaxMinutes = hourValue * 60 + 55;
			return hourMaxMinutes < startTimeMinutes + 5;
		}
		return false;
	};

	const shouldDisableMinute = (
		hourValue: number,
		minuteValue: number,
		timeType: "open" | "start" | "end",
		currentTimeId: string
	) => {
		if (timeType === "open") return false;
		const currentTime = openingTimes.find((time) => time.id === currentTimeId);
		if (!currentTime) return false;

		const openTimeMinutes = getTimeInMinutes(currentTime.openTime);
		const startTimeMinutes = getTimeInMinutes(currentTime.startTime);
		const currentTimeMinutes = hourValue * 60 + minuteValue;

		switch (timeType) {
			case "start":
				return currentTimeMinutes < openTimeMinutes + 5;
			case "end":
				return currentTimeMinutes < startTimeMinutes + 5;
			default:
				return false;
		}
	};

	return (
		<div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
			<div className="flex justify-between items-center">
				<h3 className="text-lg mb-4 font-semibold text-gray-900 dark:text-white pb-2">
					開場/開演情報
				</h3>
				<button
					type="button"
					onClick={addOpeningTime}
					className="text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 flex items-center text-sm transition-colors"
				>
					時間を追加
				</button>
			</div>

			<div className="space-y-4">
				{openingTimes.map((time) => {
					const openTimeComponents = extractTimeComponents(time.openTime);
					const startTimeComponents = extractTimeComponents(time.startTime);
					const endTimeComponents = extractTimeComponents(time.endTime);

					return (
						<div
							key={time.id}
							className="border rounded-lg p-4 dark:border-gray-700 bg-gray-50 dark:bg-gray-700"
						>
							<div className="flex mb-4 justify-between items-center">
								<input
									type="text"
									placeholder="説明（任意）"
									value={time.description || ""}
									onChange={(e) =>
										handleOpeningTimeChange(
											time.id,
											"description",
											e.target.value
										)
									}
									className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 dark:placeholder-gray-500 text-sm transition-colors"
								/>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
								{/* 開場時間 */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										開場時間
									</label>
									<div className="flex items-center gap-2">
										<select
											value={openTimeComponents.hour}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													e.target.value,
													openTimeComponents.minute
												);
												handleOpenTimeChange(time.id, newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{hourOptions.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>

										<select
											value={openTimeComponents.minute}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													openTimeComponents.hour,
													e.target.value
												);
												handleOpenTimeChange(time.id, newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{minuteOptions.map((option) => (
												<option key={option.value} value={option.value}>
													{option.label}
												</option>
											))}
										</select>
									</div>
								</div>

								{/* 開演時間 */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										開演時間
									</label>
									<div className="flex items-center gap-2">
										<select
											value={startTimeComponents.hour}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													e.target.value,
													startTimeComponents.minute
												);
												handleStartTimeChange(time.id, newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{hourOptions.map((option) => {
												const isDisabled = shouldDisableHour(
													option.hourValue,
													"start",
													time.id
												);
												return (
													<option
														key={option.value}
														value={option.value}
														disabled={isDisabled}
														className={isDisabled ? "text-gray-400" : ""}
													>
														{option.label}
													</option>
												);
											})}
										</select>

										<select
											value={startTimeComponents.minute}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													startTimeComponents.hour,
													e.target.value
												);
												handleStartTimeChange(time.id, newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{minuteOptions.map((option) => {
												const isDisabled = shouldDisableMinute(
													parseInt(startTimeComponents.hour),
													parseInt(option.value),
													"start",
													time.id
												);
												return (
													<option
														key={option.value}
														value={option.value}
														disabled={isDisabled}
														className={isDisabled ? "text-gray-400" : ""}
													>
														{option.label}
													</option>
												);
											})}
										</select>
									</div>
								</div>

								{/* 終演時間 */}
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700">
										終演時間
									</label>
									<div className="flex items-center gap-2">
										<select
											value={endTimeComponents.hour}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													e.target.value,
													endTimeComponents.minute
												);
												handleOpeningTimeChange(time.id, "endTime", newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{hourOptions.map((option) => {
												const isDisabled = shouldDisableHour(
													option.hourValue,
													"end",
													time.id
												);
												return (
													<option
														key={option.value}
														value={option.value}
														disabled={isDisabled}
														className={isDisabled ? "text-gray-400" : ""}
													>
														{option.label}
													</option>
												);
											})}
										</select>

										<select
											value={endTimeComponents.minute}
											onChange={(e) => {
												const newTime = combineTimeComponents(
													endTimeComponents.hour,
													e.target.value
												);
												handleOpeningTimeChange(time.id, "endTime", newTime);
											}}
											className="w-full px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 dark:focus:ring-violet-400 transition-colors"
										>
											{minuteOptions.map((option) => {
												const isDisabled = shouldDisableMinute(
													parseInt(endTimeComponents.hour),
													parseInt(option.value),
													"end",
													time.id
												);
												return (
													<option
														key={option.value}
														value={option.value}
														disabled={isDisabled}
														className={isDisabled ? "text-gray-400" : ""}
													>
														{option.label}
													</option>
												);
											})}
										</select>
									</div>
								</div>
							</div>

							{openingTimes.length > 1 && (
								<div className="flex justify-end">
									<button
										type="button"
										onClick={() => removeOpeningTime(time.id)}
										className="text-red-500 hover:text-red-700"
									>
										削除
									</button>
								</div>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default OpeningTimes;
